import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
    color: '#000000',
  },
  header: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 10,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#111827',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: 3,
  },
  table: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 15,
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 20,
  },
  tableHeader: {
    backgroundColor: '#F3F4F6',
    color: '#111827',
    fontWeight: 'bold',
    padding: 6,
    textAlign: 'center',
    fontSize: 9,
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: '#E5E7EB',
    flex: 1,
  },
  tableCell: {
    padding: 6,
    fontSize: 9,
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: '#E5E7EB',
    flex: 1,
    textAlign: 'center',
  },
  tableCellLeft: {
    padding: 6,
    fontSize: 9,
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: '#E5E7EB',
    flex: 2,
    textAlign: 'left',
  },
  notes: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 10,
    lineHeight: 1.3,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 9,
    color: '#9CA3AF',
    borderTop: '1px solid #E5E7EB',
    paddingTop: 8,
  },
});

const personalLoans = [
  { name: 'SBI', rate: '10.30% p.a.' },
  { name: 'ICICI Bank', rate: '10.80% – 16.50%' },
  { name: 'HDFC Bank', rate: '10.90% – 24.00%' },
  { name: 'Axis Bank', rate: '11.25% onwards' },
  { name: 'IndusInd Bank', rate: '10.49% onwards' },
  { name: 'Kotak Mahindra Bank', rate: '10.99% – 16.99%' },
  { name: 'Federal Bank', rate: '12.00% – 22.50%' },
];

const nbfcLoans = [
  { name: 'Tata Capital', rate: '10.99% – 29.99%' },
  { name: 'Aditya Birla Capital', rate: '10.99% onwards' },
  { name: 'Bajaj Finserv', rate: '11.00% onwards' },
  { name: 'Poonawalla Fincorp', rate: '9.99% onwards' },
  { name: 'MoneyTap', rate: '12.96% onwards' },
  { name: 'KreditBee', rate: '12.25% – 30.00%' },
  { name: 'InCred', rate: '16% – 36%' },
  { name: 'Shriram Finance', rate: '14% onwards' },
];

const homeLoans = [
  { name: 'Union Bank of India', rate: '7.25% – 7.35% (starting)' },
  { name: 'Canara / SBI / PNB / BOB', rate: '7.35% – 7.55%' },
  { name: 'State Bank of India', rate: '7.50% onwards' },
  { name: 'HDFC', rate: '8.15% – 8.45%' },
  { name: 'ICICI Bank', rate: '8.00% – 8.65% (digital rate)' },
  { name: 'Axis / Kotak / Federal / IDFC First', rate: '8.20% – 9.15%' },
];

const summaryData = [
  { type: 'Personal Loan', banks: '10.30% – 24%', nbfc: '9.99% – 36%, up to 44–54%', notes: 'Varies by credit score and profile' },
  { type: 'Home Loan', banks: '7.25% – 9.15%', nbfc: '~8.00% (HFCs)', notes: 'Public banks: 7.3%–7.8% after recent repo rate cuts' },
  { type: 'Loan Against Property', banks: 'N/A', nbfc: '8.90% – 13.50%', notes: 'Depends on collateral and lender' },
];

const InterestRateComparison = () => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>Comprehensive Interest Rate Comparison Report</Text>
        <Text style={styles.subtitle}>Updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} | Rates are indicative and subject to change</Text>
      </View>

      {/* Personal Loans Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>1. Personal Loan Rates (Banks & NBFCs)</Text>
        
        <View style={[styles.table, {marginBottom: 5}]}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, {flex: 2}]}>Major Banks</Text>
            <Text style={styles.tableHeader}>Interest Rate Range</Text>
          </View>
          {personalLoans.map((bank, index) => (
            <View key={`bank-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCellLeft, {paddingLeft: 10}]}>{bank.name}</Text>
              <Text style={styles.tableCell}>{bank.rate}</Text>
            </View>
          ))}
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, {flex: 2}]}>Leading NBFCs</Text>
            <Text style={styles.tableHeader}>Interest Rate Range</Text>
          </View>
          {nbfcLoans.map((nbfc, index) => (
            <View key={`nbfc-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCellLeft, {paddingLeft: 10}]}>{nbfc.name}</Text>
              <Text style={styles.tableCell}>{nbfc.rate}</Text>
            </View>
          ))}
        </View>
        
        <Text style={styles.notes}>
          <Text style={{fontWeight: 'bold'}}>Note:</Text> NBFC rates range from ~9.99% – 36%, with some cases up to 54%. Bank rates typically range from 10.30% – 24%.
        </Text>
      </View>

      {/* Home/Property Loans Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>2. Home/Property Loan Rates</Text>
        
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, {flex: 2}]}>Public & Private Banks</Text>
            <Text style={styles.tableHeader}>Interest Rate Range</Text>
          </View>
          {homeLoans.map((bank, index) => (
            <View key={`home-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCellLeft, {paddingLeft: 10}]}>{bank.name}</Text>
              <Text style={styles.tableCell}>{bank.rate}</Text>
            </View>
          ))}
        </View>
        
        <Text style={styles.notes}>
          <Text style={{fontWeight: 'bold'}}>Housing Finance Companies (HFCs) & LAP:</Text> HFCs offer rates as low as 8.00% (including floating). Loan Against Property ranges from 8.90% – 13.50%, depending on lender and collateral.
        </Text>
      </View>

      {/* Summary Table */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>3. Summary Table (Start Rates)</Text>
        
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableHeader, {flex: 1.5}]}>Loan Type</Text>
            <Text style={styles.tableHeader}>Banks</Text>
            <Text style={styles.tableHeader}>NBFCs/HFCs/LAP</Text>
            <Text style={[styles.tableHeader, {flex: 2}]}>Notes</Text>
          </View>
          {summaryData.map((item, index) => (
            <View key={`summary-${index}`} style={styles.tableRow}>
              <Text style={[styles.tableCellLeft, {paddingLeft: 10, flex: 1.5}]}>{item.type}</Text>
              <Text style={styles.tableCell}>{item.banks}</Text>
              <Text style={styles.tableCell}>{item.nbfc}</Text>
              <Text style={[styles.tableCell, {textAlign: 'left', flex: 2, fontSize: 8}]}>{item.notes}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Notes Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Notes & Trends</Text>
        <Text style={styles.notes}>
          • Interest rates fluctuate monthly—this snapshot is valid as of early July 2025.
          {'\n'}• <Text style={{fontWeight: 'bold'}}>Repo rate impact:</Text> Recent ~1% cuts by RBI have lowered home loan rates—public banks are now in the 7.3%–7.8% bracket.
          {'\n'}• <Text style={{fontWeight: 'bold'}}>Borrower profile matters:</Text> Salaried customers with strong CIBIL scores (~750+) get better rates.
          {'\n'}• <Text style={{fontWeight: 'bold'}}>Fixed vs floating:</Text> Floating rates dominate on home loans; personal loans are mostly fixed.
          {'\n'}• <Text style={{fontWeight: 'bold'}}>Additional costs:</Text> Processing fees, tenure, loan-to-value ratio, and digital offers can vary significantly.
        </Text>

        <Text style={[styles.sectionTitle, {fontSize: 12, marginTop: 15, marginBottom: 8}]}>Recommendations</Text>
        <Text style={[styles.notes, {marginBottom: 0}]}>
          • Track monthly and maintain a comparison sheet
          {'\n'}• Check lender websites for actual "starting" vs offered rates
          {'\n'}• Negotiate with banks if you have good credit history
          {'\n'}• Compare total costs including processing fees and loan tenor
        </Text>
      </View>

      <View style={styles.footer}>
        <Text>© {new Date().getFullYear()} White Pearl Finance - Comprehensive Interest Rate Comparison</Text>
        <Text style={{fontSize: 8, marginTop: 3}}>Source: Latest available rates from bank websites and financial portals (July 2025)</Text>
      </View>
    </Page>
  </Document>
);

export default InterestRateComparison;
